import classNames from "classnames";
import { useDialerPullState } from "../../lib/dialerPullContext";
import Toggle from "../../modules/common/Toggle";
import styles from "./AccountSearchPage.module.css";
import { useNavigate } from "react-router-dom";
import { useCallEnded, useGetListen } from "../../lib/api/queries";
import { useCallback, useEffect, useState } from "react";
import { paths } from "../../lib/router";
import { useListener } from "../../lib/useListener";
import WarningModal from "../../modules/common/modals/WarningModal/WarningModal";
import { reportError } from "../../lib/bugReporting";
import StyledButton from "../../modules/common/buttons/StyledButton";
import ConfirmationModal from "../../modules/common/modals/ConfirmationModal";
import { useLocationCallContext } from "../../lib/dialerUtil";

const strings = {
  doYouWant: "Do you want to automatically pull information from the dialer?",
  yes: "Yes, Waiting for Call",
  no: "No, Don't Wait for Calls",

  partialCallWarningLabel: "Incomplete Call Information Modal",
  partialCallWarning:
    "We did not receive enough information to identify this caller. Please use the search functionality to find their account.",

  customerDisconnect: "Customer Disconnect",
  customerDisconnectConfirm:
    "Please confirm that your current call has been disconnected and that you do not want to continue to the customer's details page.",
  customerDisconnectNo: "No, Call Still Active",
  customerDisconnectYes: "Yes, Customer Disconnect",
};

type Props = {
  setDisplayListenerWarning: React.Dispatch<React.SetStateAction<boolean>>;
};

const LISTENER_TIMEOUT_MS = 3000;
const MAX_NUM_GET_LISTEN_FAILURES = 5;

export default function DialerPull({ setDisplayListenerWarning }: Props) {
  const navigate = useNavigate();

  const locationCallContext = useLocationCallContext(true);
  const {
    initialListeningTime,
    shouldDialerPull,
    setShouldDialerPull,
    callConnected,
    callContext,
    setCallContext,
  } = useDialerPullState();

  const [isPartialWarningOpen, setIsPartialWarningOpen] = useState(
    !!locationCallContext && !locationCallContext.dwbuid,
  );

  const [isCallDisconnectedOpen, setIsCallDisconnectedOpen] = useState(false);

  const [numGetListenFailures, setNumGetListenFailures] = useState(0);
  useEffect(() => {
    if (numGetListenFailures > MAX_NUM_GET_LISTEN_FAILURES) {
      setDisplayListenerWarning(true);
    }
  }, [
    numGetListenFailures,
    setNumGetListenFailures,
    setDisplayListenerWarning,
  ]);

  const { mutateAsync: getListen } = useGetListen();

  const { mutateAsync: onCallEnded, isLoading: isCallEndedLoading } =
    useCallEnded();

  const onListenerCallback = useCallback(
    async (resetListener: () => void) => {
      if (!shouldDialerPull || callContext) {
        // If we are not in "pull from dialer" mode
        // Or if we are waiting for a hang-up or search complete on a partial call
        // Do not listen for calls
        return;
      }
      try {
        const data = await getListen(initialListeningTime);
        if (!data.customerCallContext) {
          resetListener();
          return;
        }

        setCallContext(data.customerCallContext);

        if (data.customerCallContext.dwbuid) {
          navigate(paths.accountDetails(data.customerCallContext.dwbuid), {
            state: {
              callContext: data.customerCallContext,
            },
          });
          return;
        }

        setIsPartialWarningOpen(true);
      } catch (err) {
        setNumGetListenFailures((prev) => prev + 1);
        reportError(
          new Error("Failed to get listen on dialer pull", { cause: err }),
        );
        resetListener();
      }
    },
    [
      shouldDialerPull,
      initialListeningTime,
      callContext,
      getListen,
      navigate,
      setCallContext,
    ],
  );

  useListener({ onListenerCallback, listenerTimeoutMs: LISTENER_TIMEOUT_MS });

  useEffect(() => {
    if (locationCallContext && locationCallContext.dwbuid) {
      navigate(paths.accountDetails(locationCallContext.dwbuid), {
        state: {
          callContext: locationCallContext,
        },
      });
    }
  }, [locationCallContext, navigate]);

  const onCallDisconnected = async () => {
    try {
      await onCallEnded({ callContextId: callContext?.id ?? -1 });
    } catch (err) {
      reportError(
        new Error(`Failed to hang up on call ${callContext?.id}`, {
          cause: err,
        }),
      );
    } finally {
      setCallContext(null);
    }
  };

  return (
    <>
      <form
        className={classNames(
          styles.searchContainer,
          styles.dialerPullContainer,
        )}
      >
        <h2 className={classNames(styles.heading, styles.searchHeading)}>
          {strings.doYouWant}
        </h2>
        <div
          className={classNames(
            styles.dialerToggleContainer,
            styles.dialerHangUpContainer,
          )}
        >
          <div className={styles.dialerToggleContainer}>
            <Toggle
              disabled={callConnected}
              onChange={(e) => setShouldDialerPull(e.target.checked)}
              checked={shouldDialerPull}
              aria-label={shouldDialerPull ? strings.yes : strings.no}
            />
            <div
              className={classNames({
                [styles.dialerLabel]: true,
                [styles.dialerLabelNone]: !shouldDialerPull,
                [styles.dialerLabelWaiting]: shouldDialerPull,
                [styles.dialerDisabled]: callConnected,
              })}
            >
              {shouldDialerPull ? strings.yes : strings.no}
            </div>
          </div>
          {callConnected && (
            <StyledButton
              type="button"
              variant="danger"
              onClick={() => setIsCallDisconnectedOpen(true)}
              isLoading={isCallEndedLoading}
            >
              {strings.customerDisconnect}
            </StyledButton>
          )}
        </div>
      </form>
      <WarningModal
        modalLabel={strings.partialCallWarningLabel}
        isOpen={isPartialWarningOpen}
        body={strings.partialCallWarning}
        onRequestClose={() => setIsPartialWarningOpen(false)}
      />
      <ConfirmationModal
        title={strings.customerDisconnect}
        message={strings.customerDisconnectConfirm}
        cancelText={strings.customerDisconnectNo}
        confirmText={strings.customerDisconnectYes}
        isOpen={isCallDisconnectedOpen}
        onRequestClose={() => setIsCallDisconnectedOpen(false)}
        onConfirm={() => {
          setIsCallDisconnectedOpen(false);
          onCallDisconnected();
        }}
      />
    </>
  );
}
